'use client';

import { useRef, type RefObject } from 'react';
import { Modal } from '@/app/components/Modal';
import { useModalContentHeight } from '@/app/components/Modal/hooks/useModalContentHeight';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import type { DuplicateClientMatch } from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';
import { DuplicateClientCard } from './components/DuplicateClientCard';
import { getDuplicateWarningContent } from './utils/getDuplicateWarningContent';

const MODAL_PANEL_MAX_HEIGHT = 'max-h-[min(800px,90vh)]';

interface DuplicateClientWarningModalProps {
  isOpen: boolean;
  matches: DuplicateClientMatch[];
  searchedEmail: string;
  searchedFullName: string;
  isContinueLoading?: boolean;
  onClose: () => void;
  onContinue: () => void;
}

export function DuplicateClientWarningModal({
  isOpen,
  matches,
  searchedEmail,
  searchedFullName,
  isContinueLoading = false,
  onClose,
  onContinue,
}: DuplicateClientWarningModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { title, description, sections } = getDuplicateWarningContent(matches, searchedEmail, searchedFullName);
  const { hasScrollableContent, setContentRef } = useModalContentHeight<HTMLDivElement>(
    isOpen,
    matches.length,
    contentRef as RefObject<HTMLDivElement>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div
        className={cn(
          'pointer-events-auto flex w-full min-w-[550px] max-w-[550px] flex-col overflow-hidden rounded-3xl bg-white',
          'max-md:w-[calc(100vw-2rem)] max-md:min-w-0 max-md:max-w-full max-md:rounded-[14px]',
          MODAL_PANEL_MAX_HEIGHT
        )}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 flex-col items-center gap-1.5 px-9 pt-11.5 text-center max-md:px-4 max-md:pt-6">
          <h2 className="light text-blue-900 max-md:max-w-48">{title}</h2>
          <p className="max-w-120 text-body-regular text-grey-700 max-md:text-sm max-md:leading-[150%]">
            {description}
          </p>
        </div>

        <div
          ref={setContentRef}
          className="flex min-h-0 w-full flex-1 flex-col gap-6 overflow-y-auto px-9 pt-6 pb-5 max-md:gap-4 max-md:px-4 max-md:pt-4 max-md:pb-0"
        >
          {sections.map((section) => (
            <div key={section.id} className="flex flex-col gap-3 text-left max-md:gap-2">
              <p className="text-body-regular whitespace-nowrap truncate max-md:whitespace-normal max-md:overflow-visible max-md:text-clip max-md:text-sm max-md:leading-[140%]">
                <span className="text-grey-700">{section.label.prefix}</span>
                <span className="text-blue-300 font-medium max-md:leading-[150%]">
                  {section.label.value ? <span>{` ${section.label.value}`}</span> : null}
                </span>
              </p>
              <div className="flex flex-col gap-3">
                {section.clients.map((client) => (
                  <DuplicateClientCard
                    key={`${section.id}-${client.id}`}
                    client={client}
                    highlightField={section.highlightField}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className={cn(
            'mx-9 flex shrink-0 items-center justify-between gap-5 bg-white pb-[46px] pt-[30px]',
            'max-md:mx-4 max-md:gap-4 max-md:pt-5 max-md:pb-6',
            hasScrollableContent && 'border-t border-grey-400'
          )}
        >
          <BaseButton
            type="button"
            intent="secondary"
            size="full"
            className="h-10 max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:px-2 max-md:text-sm"
            onClick={onClose}
            disabled={isContinueLoading}
          >
            Cancel
          </BaseButton>
          <BaseButton
            type="button"
            intent="primary"
            size="full"
            className="h-10 max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:border max-md:border-transparent max-md:px-2 max-md:text-sm"
            onClick={onContinue}
            disabled={isContinueLoading}
          >
            {isContinueLoading ? 'Continuing…' : 'Continue Anyway'}
          </BaseButton>
        </div>
      </div>
    </Modal>
  );
}
